import React, { useState, useEffect, useMemo } from 'react';
import { View, Text, TextInput, StyleSheet, TouchableOpacity, ActivityIndicator, FlatList } from 'react-native';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged, signOut } from 'firebase/auth';
import { app } from '../firebase.config';
import { setDocument, getFileURL } from '../db_calls';
import { getCustomerThumbnails } from '../db_calls_wrapper';
import { useCustomer } from '../stores';

const auth = getAuth(app);

// Helper function to format phone number for display
const formatPhoneNumber = (value) => {
  const numbers = value.replace(/\D/g, '');
  const limited = numbers.slice(0, 10);
  
  if (limited.length <= 3) {
    return limited;
  } else if (limited.length <= 6) {
    return `${limited.slice(0, 3)}-${limited.slice(3)}`;
  } else {
    return `${limited.slice(0, 3)}-${limited.slice(3, 6)}-${limited.slice(6)}`;
  }
};

// Helper function to get unformatted phone number for database
const getUnformattedPhone = (formattedPhone) => {
  return formattedPhone.replace(/\D/g, '');
};

const DEV_MODE = process.env.NODE_ENV === 'development';

const Web_Base = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  // Form inputs - Pre-populate in development mode
  const [firstName, setFirstName] = useState(DEV_MODE ? "Ricky" : "");
  const [lastName, setLastName] = useState(DEV_MODE ? "Bobby" : "");
  const [phone, setPhone] = useState(DEV_MODE ? "239-336-9177" : "");
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Photo gallery state
  const [showGallery, setShowGallery] = useState(false);
  const [thumbnails, setThumbnails] = useState([]);
  const [loadingThumbnails, setLoadingThumbnails] = useState(false);

  // Zustand store
  const customers = useCustomer((state) => state.customers);
  const currentCustomer = useCustomer((state) => state.customer);
  const addCustomer = useCustomer((state) => state.addCustomer);
  const setCustomer = useCustomer((state) => state.setCustomer);

  // Search results based on form inputs
  const searchResults = useMemo(() => {
    if (!firstName && !lastName && !phone) {
      return customers;
    }

    const unformattedPhone = getUnformattedPhone(phone);

    const filtered = customers.filter((customer) => {
      const firstNameMatch =
        !firstName ||
        customer.firstName?.toLowerCase().includes(firstName.toLowerCase());

      const lastNameMatch =
        !lastName ||
        customer.lastName?.toLowerCase().includes(lastName.toLowerCase());

      const phoneMatch = !phone || customer.phone?.includes(unformattedPhone);

      return firstNameMatch && lastNameMatch && phoneMatch;
    });

    return filtered;
  }, [firstName, lastName, phone, customers]);

  useEffect(() => {
    console.log("Web_Base component mounted (Desktop view)");
    console.log("📦 Customers are loaded from cache globally via App.js");

    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      console.log(
        "Auth state changed:",
        currentUser ? currentUser.email : "No user"
      );
      setUser(currentUser);
      setLoading(false);

      if (!currentUser) {
        console.log("Not authenticated, redirecting to /login");
        navigate("/login");
      } else {
        console.log("User authenticated (Desktop view):", currentUser.email);
      }
    });

    return () => {
      unsubscribeAuth();
    };
  }, [navigate]);

  const handlePhoneChange = (text) => {
    const formatted = formatPhoneNumber(text);
    setPhone(formatted);
  };

  const handleSubmit = async () => {
    if (!firstName.trim()) {
      alert("Please enter a first name");
      return;
    }
    if (!lastName.trim()) {
      alert("Please enter a last name");
      return;
    }
    if (!phone.trim()) {
      alert("Please enter a phone number");
      return;
    }

    const unformattedPhone = getUnformattedPhone(phone);

    if (unformattedPhone.length !== 10) {
      alert("Please enter a valid 10-digit phone number");
      return;
    }

    setSaving(true);
    setSaveSuccess(false);

    try {
      // Prepare customer data - minimal fields only
      const customerData = {
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phone: unformattedPhone,
        photoCount: 0,
        createdAt: new Date(),
      };

      await setDocument("customers", unformattedPhone, customerData, false);

      // Prepare full customer object with computed fullName
      const fullCustomerData = {
        id: unformattedPhone,
        ...customerData,
        fullName: `${firstName.trim()} ${lastName.trim()}`.trim(),
      };

      const existingCustomerIndex = customers.findIndex(
        (c) => c.id === unformattedPhone
      );

      if (existingCustomerIndex >= 0) {
        const updateCustomer = useCustomer.getState().updateCustomer;
        updateCustomer(unformattedPhone, customerData);
      } else {
        addCustomer(fullCustomerData);
      }

      setCustomer(fullCustomerData);

      setFirstName("");
      setLastName("");
      setPhone("");

      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (error) {
      console.error("Error saving customer:", error);
      alert("Failed to save customer: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleLoadGallery = async () => {
    if (!currentCustomer || !currentCustomer.phone) {
      alert("Please select a customer first");
      return;
    }

    setLoadingThumbnails(true);
    setShowGallery(true);

    try {
      console.log("📸 Loading photo gallery for:", currentCustomer.fullName);
      const customerThumbnails = await getCustomerThumbnails(
        currentCustomer.phone,
        10
      );

      // Get full-size URLs for each thumbnail
      const thumbnailsWithFullSize = await Promise.all(
        customerThumbnails.map(async (thumb) => {
          try {
            const fullSizeUrl = await getFileURL(thumb.fullSizePath);
            return {
              ...thumb,
              fullSizeUrl: fullSizeUrl,
            };
          } catch (error) {
            console.warn(
              "Could not get full-size URL for:",
              thumb.fullSizePath
            );
            return {
              ...thumb,
              fullSizeUrl: thumb.thumbnailUrl, // Fallback to thumbnail
            };
          }
        })
      );

      setThumbnails(thumbnailsWithFullSize);
      console.log(
        `✅ Loaded ${thumbnailsWithFullSize.length} thumbnails with full-size URLs`
      );
    } catch (error) {
      console.error("Error loading gallery:", error);
      alert("Failed to load photos: " + error.message);
    } finally {
      setLoadingThumbnails(false);
    }
  };

  const handleCloseGallery = () => {
    setShowGallery(false);
    setThumbnails([]);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  if (loading || !user) {
    return (
      <View style={styles.container}>
        <Text style={styles.title}>Loading...</Text>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Customer Input Form */}
      <View style={styles.formContainer}>
        <Text style={styles.formTitle}>Customer Information</Text>

        {saveSuccess && (
          <View style={styles.successMessage}>
            <Text style={styles.successText}>
              ✓ Customer saved successfully!
            </Text>
          </View>
        )}

        {/* Search Results Count */}
        {(firstName || lastName || phone) && (
          <View style={styles.searchInfo}>
            <Text style={styles.searchInfoText}>
              Found {searchResults.length} matching customer
              {searchResults.length !== 1 ? "s" : ""}
            </Text>
          </View>
        )}

        <View style={styles.inputGroup}>
          <Text style={styles.label}>First Name</Text>
          <TextInput
            style={styles.input}
            placeholder="Enter first name"
            value={firstName}
            onChangeText={setFirstName}
            autoCapitalize="words"
            editable={!saving}
          />
        </View>

        <View style={styles.inputGroup}>
          <Text style={styles.label}>Last Name</Text>
          <TextInput
            style={styles.input}
            placeholder="Enter last name"
            value={lastName}
            onChangeText={setLastName}
            autoCapitalize="words"
            editable={!saving}
          />
        </View>

        <View style={styles.inputGroup}>
          <Text style={styles.label}>
            Phone <Text style={styles.required}>*</Text>
          </Text>
          <TextInput
            style={styles.input}
            placeholder="xxx-xxx-xxxx (required)"
            value={phone}
            onChangeText={handlePhoneChange}
            keyboardType="phone-pad"
            maxLength={12}
            editable={!saving}
          />
          <Text style={styles.helperText}>
            Format: xxx-xxx-xxxx (saved as xxxxxxxxxx)
          </Text>
        </View>

        <TouchableOpacity dataSet={{ hover: "lift" }}
          style={[styles.submitButton, saving && styles.submitButtonDisabled]}
          onPress={handleSubmit}
          disabled={saving}
        >
          {saving ? (
            <ActivityIndicator color="#FFFFFF" />
          ) : (
            <Text style={styles.buttonText}>Save Customer</Text>
          )}
        </TouchableOpacity>
      </View>

      {/* Customer Search Results */}
      {customers.length > 0 && (
        <View style={styles.resultsContainer}>
          <Text style={styles.resultsTitle}>
            {firstName || lastName || phone
              ? `Search Results (${searchResults.length})`
              : `All Customers (${customers.length})`}
          </Text>

          <FlatList
            data={searchResults}
            keyExtractor={(item) => item.id}
            renderItem={({ item }) => {
              const isSelected = currentCustomer?.id === item.id;

              return (
                <TouchableOpacity
                  style={styles.customerItem}
                  onPress={() => {
                    setCustomer(item);
                    setFirstName(item.firstName);
                    setLastName(item.lastName);
                    setPhone(formatPhoneNumber(item.phone));
                  }}
                >
                  <View style={styles.customerInfo}>
                    <Text style={styles.customerName}>{item.fullName}</Text>
                    <Text style={styles.customerPhone}>
                      {formatPhoneNumber(item.phone)} • {item.photoCount || 0}{" "}
                      photo{(item.photoCount || 0) !== 1 ? "s" : ""}
                    </Text>
                  </View>
                  {isSelected && <Text style={styles.selectText}>✓</Text>}
                </TouchableOpacity>
              );
            }}
            ListEmptyComponent={
              <View style={styles.emptyContainer}>
                <Text style={styles.emptyText}>
                  {firstName || lastName || phone
                    ? "No matching customers found"
                    : "No customers yet"}
                </Text>
              </View>
            }
            style={styles.flatList}
          />
        </View>
      )}

      {/* Action Buttons */}
      <View style={styles.actionButtonsContainer}>
        <TouchableOpacity dataSet={{ hover: "lift" }}
          style={[
            styles.viewPhotosButton,
            !currentCustomer && styles.viewPhotosButtonDisabled,
          ]}
          onPress={handleLoadGallery}
          disabled={!currentCustomer}
        >
          <Text style={styles.buttonText}>
            📸 View Photos
            {currentCustomer
              ? ` - ${currentCustomer.photoCount || 0} photo${
                  (currentCustomer.photoCount || 0) !== 1 ? "s" : ""
                }`
              : ""}
          </Text>
        </TouchableOpacity>

        <TouchableOpacity dataSet={{ hover: "lift" }}
          style={styles.mobileViewButton}
          onPress={() => navigate("/today")}
        >
          <Text style={styles.buttonText}>📱 Open Mobile View</Text>
        </TouchableOpacity>

        <TouchableOpacity dataSet={{ hover: "lift" }}
          style={styles.lockManagementButton}
          onPress={() => navigate("/locks-desktop")}
        >
          <Text style={styles.buttonText}>🔒 Lock Management</Text>
        </TouchableOpacity>
      </View>

      {/* Photo Gallery Modal */}
      {showGallery && (
        <View style={styles.galleryOverlay}>
          <View style={styles.galleryContainer}>
            <View style={styles.galleryHeader}>
              <Text style={styles.galleryTitle}>
                {currentCustomer?.fullName}'s Photos
              </Text>
              <TouchableOpacity onPress={handleCloseGallery}>
                <Text style={styles.closeButton}>✕</Text>
              </TouchableOpacity>
            </View>

            {loadingThumbnails ? (
              <View style={styles.loadingContainer}>
                <ActivityIndicator size="large" color="#0E7C6B" />
                <Text style={styles.loadingText}>Loading photos...</Text>
              </View>
            ) : (
              <FlatList
                data={thumbnails}
                keyExtractor={(item) => item.name}
                numColumns={3}
                renderItem={({ item }) => (
                  <TouchableOpacity
                    style={styles.thumbnailItem}
                    onPress={() => {
                      console.log(
                        "🖼️ Opening full-size image:",
                        item.fullSizeUrl
                      );
                      window.open(
                        item.fullSizeUrl || item.thumbnailUrl,
                        "_blank"
                      );
                    }}
                  >
                    <img
                      src={item.thumbnailUrl || item.url}
                      alt={item.name}
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        borderRadius: 8,
                      }}
                    />
                  </TouchableOpacity>
                )}
                ListEmptyComponent={
                  <View style={styles.emptyGalleryContainer}>
                    <Text style={styles.emptyGalleryIcon}>📷</Text>
                    <Text style={styles.emptyGalleryText}>No photos yet</Text>
                    <Text style={styles.emptyGallerySubtext}>
                      Take photos on the mobile app to see them here
                    </Text>
                  </View>
                }
                contentContainerStyle={styles.galleryGrid}
              />
            )}
          </View>
        </View>
      )}

      <TouchableOpacity dataSet={{ hover: "lift" }} style={styles.logoutButton} onPress={handleLogout}>
        <Text style={styles.buttonText}>Sign Out</Text>
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: "center",
    backgroundColor: "#F6F8F7",
    paddingHorizontal: 40,
    paddingVertical: 20,
  },
  formContainer: {
    width: "100%",
    maxWidth: 600,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 20,
    marginVertical: 20,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
  },
  formTitle: {
    fontSize: 20,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 20,
  },
  successMessage: {
    backgroundColor: "#E4F4EC",
    padding: 12,
    borderRadius: 6,
    marginBottom: 20,
    borderLeftWidth: 4,
    borderLeftColor: "#1F8A5B",
  },
  successText: {
    color: "#1F8A5B",
    fontSize: 14,
    fontWeight: "600",
  },
  searchInfo: {
    backgroundColor: "#FDF3E3",
    padding: 10,
    borderRadius: 5,
    marginBottom: 15,
    borderLeftWidth: 4,
    borderLeftColor: "#B4761B",
  },
  searchInfoText: {
    color: "#7A4E12",
    fontSize: 13,
    fontWeight: "600",
  },
  inputGroup: {
    marginBottom: 20,
  },
  label: {
    fontSize: 14,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 8,
  },
  required: {
    color: "#C4453D",
    fontSize: 14,
    fontWeight: "bold",
  },
  input: {
    height: 50,
    borderWidth: 1,
    borderColor: "#DDE3E2",
    borderRadius: 6,
    paddingHorizontal: 15,
    fontSize: 16,
    backgroundColor: "#F4F6F6",
    outlineStyle: "none",
  },
  helperText: {
    fontSize: 12,
    color: "#8A9794",
    marginTop: 5,
  },
  submitButton: {
    backgroundColor: "#1F8A5B",
    paddingVertical: 15,
    borderRadius: 6,
    alignItems: "center",
    marginTop: 10,
  },
  submitButtonDisabled: {
    backgroundColor: "#1F8A5B",
  },
  resultsContainer: {
    width: "100%",
    maxWidth: 600,
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    padding: 20,
    marginVertical: 20,
    maxHeight: 400,
    boxShadow: "0 1px 3px rgba(15, 26, 25, 0.07), 0 6px 16px rgba(15, 26, 25, 0.08)",
    elevation: 5,
  },
  resultsTitle: {
    fontSize: 18,
    fontWeight: "bold",
    color: "#2E3A38",
    marginBottom: 15,
  },
  flatList: {
    maxHeight: 300,
  },
  customerItem: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    padding: 15,
    backgroundColor: "#F4F6F6",
    borderRadius: 6,
    marginBottom: 10,
    borderWidth: 1,
    borderColor: "#DDE3E2",
  },
  customerInfo: {
    flex: 1,
  },
  customerName: {
    fontSize: 16,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 4,
  },
  customerPhone: {
    fontSize: 14,
    color: "#5C6B68",
  },
  selectText: {
    color: "#1F8A5B",
    fontSize: 24,
    fontWeight: "bold",
  },
  emptyContainer: {
    paddingVertical: 40,
    alignItems: "center",
  },
  emptyText: {
    fontSize: 14,
    color: "#8A9794",
    textAlign: "center",
  },
  actionButtonsContainer: {
    width: "100%",
    maxWidth: 600,
    marginTop: 20,
    gap: 10,
  },
  viewPhotosButton: {
    backgroundColor: "#0E7C6B",
    paddingHorizontal: 30,
    paddingVertical: 15,
    borderRadius: 6,
    alignItems: "center",
  },
  viewPhotosButtonDisabled: {
    backgroundColor: "#AEB8B6",
  },
  mobileViewButton: {
    backgroundColor: "#5C6B68",
    paddingHorizontal: 30,
    paddingVertical: 15,
    borderRadius: 6,
    alignItems: "center",
    marginBottom: 12,
  },
  lockManagementButton: {
    backgroundColor: "#5C6B68",
    paddingHorizontal: 30,
    paddingVertical: 15,
    borderRadius: 6,
    alignItems: "center",
  },
  galleryOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    zIndex: 1000,
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
  },
  galleryContainer: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    width: "100%",
    maxWidth: 800,
    maxHeight: "90%",
    padding: 20,
  },
  galleryHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
    paddingBottom: 15,
    borderBottomWidth: 1,
    borderBottomColor: "#DDE3E2",
  },
  galleryTitle: {
    fontSize: 24,
    fontWeight: "bold",
    color: "#2E3A38",
  },
  closeButton: {
    fontSize: 32,
    color: "#5C6B68",
    fontWeight: "bold",
    cursor: "pointer",
  },
  loadingContainer: {
    paddingVertical: 60,
    alignItems: "center",
  },
  loadingText: {
    marginTop: 15,
    fontSize: 16,
    color: "#5C6B68",
  },
  galleryGrid: {
    padding: 10,
  },
  thumbnailItem: {
    width: "30%",
    aspectRatio: 1,
    margin: "1.5%",
    borderRadius: 8,
    overflow: "hidden",
    backgroundColor: "#EDF1F0",
    cursor: "pointer",
  },
  emptyGalleryContainer: {
    paddingVertical: 60,
    alignItems: "center",
  },
  emptyGalleryIcon: {
    fontSize: 64,
    marginBottom: 15,
  },
  emptyGalleryText: {
    fontSize: 18,
    fontWeight: "600",
    color: "#2E3A38",
    marginBottom: 10,
  },
  emptyGallerySubtext: {
    fontSize: 14,
    color: "#8A9794",
    textAlign: "center",
  },
  logoutButton: {
    backgroundColor: "#C4453D",
    paddingHorizontal: 30,
    paddingVertical: 15,
    borderRadius: 6,
    alignItems: "center",
    marginTop: 20,
  },
  buttonText: {
    color: "#FFFFFF",
    fontSize: 16,
    fontWeight: "600",
  },
});

export default Web_Base;

